import * as Sentry from '@sentry/react';

import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';

const ENVIRONMENT = window.location.hostname.includes(`staging-`) ? `staging` : APP_ENV || `development`;
const IS_PRODUCTION = ENVIRONMENT === `production`;
const IS_DEVELOPMENT = ENVIRONMENT === `development`;

const MIXPANEL_DOMAINS = [
  `https://cdn.mxpnl.com`,
  /^https:\/\/(.+\.)?mixpanel\.(com|org|)/,

  // for dev environments where a dev server is serving bundle files
  ...(IS_DEVELOPMENT ? [`webpack-internal://`, /localhost:(.+)\/vite/] : []),
];

const ZENDESK_DOMAINS = [
  /^https:\/\/(.+\.)?zdassets\.(com|)/,
  /^https:\/\/(.+\.)?zendesk\.(com|)/,
  /^https:\/\/(.+\.)?zopim\.(com|io|)/,
];

const IGNORED_ERROR_CLASSES = new Set([`AbortError`, `ApiResponseError`, `ResponseError`, `MpFetchError`]);

const IGNORED_MESSAGES = [
  `Security Policy Violation.*location.*-extension.*`,
  `ResizeObserver loop limit exceeded`,
  `ResizeObserver loop completed with undelivered notifications`,
  `Failed to fetch`,
];

const IGNORED_JS_SDK_MESSAGES = [
  ...[0, 403, 502, `502 Bad Gateway`].map((code) => `Bad HTTP status: ${code}`),
  /Error; retry in \d+ ms/,
  `Error; retry in 10000 ms`,
];

window.sentry = Sentry.init({
  debug: false,
  environment: ENVIRONMENT,
  stackParser: Sentry.defaultStackParser,
  transport: Sentry.makeFetchTransport,
  dsn: `https://8da60ce5d69f3663b76685b5a017bfd4@o81318.ingest.us.sentry.io/4508649566830592`,
  sendDefaultPii: false,
  allowUrls: MIXPANEL_DOMAINS,
  // disables session replay by default
  replaysSessionSampleRate: 0,
  // makes sure sessions with errors are sent with a sample rate of 10%
  replaysOnErrorSampleRate: 0.1,
  ignoreErrors: [
    ...IGNORED_MESSAGES,
    ...IGNORED_JS_SDK_MESSAGES,
    `Error retrieving queue`,
    `Error saving queue`,
    `Network timeout; retrying`,
    `Event \`CustomEvent\` (type=unhandledrejection) captured as promise rejection`,
    `400 - Bad request`,
    `401 - Unauthenticated`,
    `402 - Request failed`,
    `404 - Resource not found`,
    `412 - Request failed`,
    `500 - Internal server error`,
    `502 - Bad gateway`,
    `504 - Gateway timeout`,
    // floating-ui lib code error
    `Cannot read properties of null (reading 'getBoundingClientRect')`,
    `getSeriesDisplaySegmentColumns: length mismatch between headers and headerTypes`,
    `Load failed`,
  ],

  beforeSend(event, hint) {
    const {originalException} = hint;

    // @ts-ignore
    const className = originalException?.constructor?.name;

    if (className && IGNORED_ERROR_CLASSES.has(className)) {
      return null;
    }

    return event;
  },

  integrations: (integrations) => {
    return [
      /** disables the http integration, we do not care about failed HTTP responses in Sentry */
      ...integrations.filter((integration) => integration.name !== `HttpClient`),

      /** for production environments, records sessions leading up to an error
       * We exclude Zendesk requests from Sentry tracing because the
       * Zendesk Web Widget performs many internal network calls when opening.
       */
      ...(IS_PRODUCTION
        ? [
            Sentry.replayIntegration({
              networkDetailDenyUrls: ZENDESK_DOMAINS,
              // Prevent rrweb from capturing or patching Zendesk iframe content,
              // which interferes with the Zendesk live chat WebSocket connection.
              block: [`iframe#webWidget`, `iframe#launcher`],
            }),
          ]
        : []),

      /** filters out exceptions raised from external sources */
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [`web-client`],
        behaviour: IS_PRODUCTION
          ? `drop-error-if-exclusively-contains-third-party-frames`
          : `apply-tag-if-contains-third-party-frames`,
      }),

      /** when user settings allow for it, send the user email */
      {
        name: `GDPRIntegration`,
        async setup() {
          const userIdentity = userCookieUtils.getUserIdentityFromCookie();
          if (window.gdprData && userIdentity) {
            const gdprData = window.gdprData;
            Sentry.setUser({id: userIdentity.id});
            await gdprData.waitUntilReady();
            if (gdprData.allowTracking) {
              Sentry.setUser({
                id: userIdentity.id,
                email: userIdentity.email,
              });
            }
          }
        },
      },
    ];
  },
});
